import type { ProfileFieldsWire } from '../api/adminOps';
import { changedFieldCount, profileDiff } from '../pages/charityProfile.logic';

interface OrganizationProfileDiffProps {
  live: ProfileFieldsWire;
  draft: ProfileFieldsWire | null;
}

const EMPTY_VALUE = '—';

function Value({ value }: { value: string | null }) {
  if (value === null || value === '') return <span className="text-gray-text">{EMPTY_VALUE}</span>;
  return <span className="whitespace-pre-wrap break-words">{value}</span>;
}

/**
 * DM-7976: a charity's live profile beside the draft under review, field by
 * field. Changed rows are highlighted; the values are rendered as text only,
 * because they were typed by the charity and are exactly what is being judged.
 */
export default function OrganizationProfileDiff({ live, draft }: OrganizationProfileDiffProps) {
  const rows = profileDiff(live, draft);
  const changed = changedFieldCount(rows);
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-text">
        {draft === null
          ? 'No draft is pending; the live profile is shown.'
          : changed === 0
            ? 'The draft repeats the live profile; no field changes.'
            : `${changed} changed field${changed === 1 ? '' : 's'}`}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-border text-left text-xs text-gray-text">
            <th scope="col" className="py-1 pr-2 font-medium">
              Field
            </th>
            <th scope="col" className="py-1 pr-2 font-medium">
              Live
            </th>
            <th scope="col" className="py-1 font-medium">
              Draft
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.key}
              data-changed={row.changed ? 'true' : 'false'}
              className={`border-b border-gray-border/50 align-top ${
                row.changed ? 'bg-yellow-500/10' : ''
              }`}
            >
              <th scope="row" className="py-1 pr-2 text-left text-xs font-medium text-gray-text">
                {row.label}
              </th>
              <td className="py-1 pr-2 text-white">
                <Value value={row.live} />
              </td>
              <td className={`py-1 ${row.changed ? 'text-yellow-300' : 'text-white'}`}>
                {draft === null ? (
                  <span className="text-gray-text">{EMPTY_VALUE}</span>
                ) : (
                  <Value value={row.draft} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
